import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { ShortListItem } from "@shared/schema";

export interface LocalShortListItem {
  id: string;
  type: "action_card" | "article" | "company";
  actionCardId?: string;
  articleId?: string;
  companyId?: string;
  title: string;
  category?: string;
  image?: string;
  notes?: string;
  addedAt: string;
}

interface ShortListContextType {
  items: (LocalShortListItem | ShortListItem)[];
  isLoading: boolean;
  addItem: (item: Omit<LocalShortListItem, "id" | "addedAt">) => Promise<void>;
  removeItem: (id: string) => Promise<void>;
  updateNotes: (id: string, notes: string) => Promise<void>;
  clearList: () => void;
  isInList: (itemId: string, type: string) => boolean;
  itemCount: number;
  isDrawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;
  isGuest: boolean;
  syncGuestItems: () => Promise<void>;
}

const ShortListContext = createContext<ShortListContextType | undefined>(undefined);

const GUEST_STORAGE_KEY = "deep_media_short_list";
const MAX_GUEST_ITEMS = 5;
const MAX_REGISTERED_ITEMS = 50;

export function ShortListProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [guestItems, setGuestItems] = useState<LocalShortListItem[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [hasSyncedOnLogin, setHasSyncedOnLogin] = useState(false);

  const isGuest = !isAuthenticated;

  useEffect(() => {
    const stored = localStorage.getItem(GUEST_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setGuestItems(parsed);
        }
      } catch (e) {
        console.error("Failed to parse short list from storage");
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(guestItems));
  }, [guestItems]);

  const { data: dbItems = [], isLoading: dbLoading } = useQuery<ShortListItem[]>({
    queryKey: ["/api/short-list"],
    enabled: isAuthenticated,
  });

  const addMutation = useMutation({
    mutationFn: async (item: Omit<LocalShortListItem, "id" | "addedAt">) => {
      return apiRequest("POST", "/api/short-list", {
        itemType: item.type,
        actionCardId: item.actionCardId,
        articleId: item.articleId,
        companyId: item.companyId,
        itemTitle: item.title,
        itemCategory: item.category,
        itemImage: item.image,
        notes: item.notes,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/short-list"] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/short-list/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/short-list"] });
    },
  });

  const updateNotesMutation = useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes: string }) => {
      return apiRequest("PATCH", `/api/short-list/${id}`, { notes });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/short-list"] });
    },
  });

  const syncMutation = useMutation({
    mutationFn: async (items: Omit<LocalShortListItem, "id" | "addedAt">[]) => {
      return apiRequest("POST", "/api/short-list/sync", {
        items: items.map((item) => ({
          itemType: item.type,
          actionCardId: item.actionCardId,
          articleId: item.articleId,
          companyId: item.companyId,
          itemTitle: item.title,
          itemCategory: item.category,
          itemImage: item.image,
          notes: item.notes,
        })),
      });
    },
    onSuccess: () => {
      setGuestItems([]);
      localStorage.removeItem(GUEST_STORAGE_KEY);
      queryClient.invalidateQueries({ queryKey: ["/api/short-list"] });
    },
  });

  useEffect(() => {
    if (isAuthenticated && guestItems.length > 0 && !hasSyncedOnLogin && !authLoading) {
      setHasSyncedOnLogin(true);
      syncMutation.mutate(guestItems);
    }
  }, [isAuthenticated, guestItems, hasSyncedOnLogin, authLoading]);

  const addItem = useCallback(async (item: Omit<LocalShortListItem, "id" | "addedAt">) => {
    if (isGuest) {
      setGuestItems((prev) => {
        const itemId = item.actionCardId || item.articleId || item.companyId;
        if (prev.some((i) => (i.actionCardId || i.articleId || i.companyId) === itemId)) {
          return prev;
        }
        if (prev.length >= MAX_GUEST_ITEMS) {
          return prev;
        }
        return [...prev, {
          ...item,
          id: `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          addedAt: new Date().toISOString(),
        }];
      });
    } else {
      await addMutation.mutateAsync(item);
    }
  }, [isGuest, addMutation]);

  const removeItem = useCallback(async (id: string) => {
    if (isGuest || id.startsWith("guest_")) {
      setGuestItems((prev) => prev.filter((item) => item.id !== id));
    } else {
      await removeMutation.mutateAsync(id);
    }
  }, [isGuest, removeMutation]);

  const updateNotes = useCallback(async (id: string, notes: string) => {
    if (isGuest || id.startsWith("guest_")) {
      setGuestItems((prev) => prev.map((item) => 
        item.id === id ? { ...item, notes } : item
      ));
    } else {
      await updateNotesMutation.mutateAsync({ id, notes });
    }
  }, [isGuest, updateNotesMutation]);

  const clearList = useCallback(() => {
    if (isGuest) {
      setGuestItems([]);
    }
  }, [isGuest]);

  const isInList = useCallback((itemId: string, type: string) => {
    const items = isGuest ? guestItems : dbItems;
    return items.some((item) => {
      if (type === "action_card") {
        return "actionCardId" in item ? item.actionCardId === itemId : false;
      }
      if (type === "article") {
        return "articleId" in item ? item.articleId === itemId : false;
      }
      if (type === "company") {
        return "companyId" in item ? item.companyId === itemId : false;
      }
      return false;
    });
  }, [isGuest, guestItems, dbItems]);

  const syncGuestItems = useCallback(async () => {
    if (isAuthenticated && guestItems.length > 0) {
      await syncMutation.mutateAsync(guestItems);
    }
  }, [isAuthenticated, guestItems, syncMutation]);

  const openDrawer = () => setIsDrawerOpen(true);
  const closeDrawer = () => setIsDrawerOpen(false);
  const toggleDrawer = () => setIsDrawerOpen((prev) => !prev);

  const items = isGuest ? guestItems : dbItems;
  const isLoading = authLoading || (isAuthenticated && dbLoading);
  const itemCount = items.length;
  const maxItems = isGuest ? MAX_GUEST_ITEMS : MAX_REGISTERED_ITEMS;

  return (
    <ShortListContext.Provider
      value={{
        items,
        isLoading,
        addItem,
        removeItem,
        updateNotes,
        clearList,
        isInList,
        itemCount,
        isDrawerOpen,
        openDrawer,
        closeDrawer,
        toggleDrawer,
        isGuest,
        syncGuestItems,
      }}
    >
      {children}
    </ShortListContext.Provider>
  );
}

export function useShortList() {
  const context = useContext(ShortListContext);
  if (context === undefined) {
    throw new Error("useShortList must be used within a ShortListProvider");
  }
  return context;
}
